<template>
  <div
      tabindex="-1"
      id="content"
      class="o-container  o-container--mw800  u-text-center  u-focus-none">

    <div
        ref="focusTarget"
        class="o-flex  o-flex-column  u-h-screen  u-items-center  u-justify-center">

      <h1 class="c-header__title  u-mb-48  u-text-highlight  u-caps">

        <SvgSprite
            icon="twd-logo"
            class="u-fill-current"
            width="30"
            height="30"
            aria-hidden="true">
        </SvgSprite>

        Tonight We Dance
      </h1>

      <h2 class="c-album__title  u-block  u-mb-24  u-text-highlight  u-lh-1-1">
        Page Not Found
      </h2>

      <h3 class="c-section-heading  u-block  u-mb-48  u-text-secondary  u-ls-xwide  u-caps">
        Error 404 – This page doesn’t exist
      </h3>

      <p class="u-text-gamma">
        We are sorry, the page you requested cannot be found. The URL may be misspelled or the page you’re looking for is no longer available.
      </p>

      <p class="u-mb-0  u-text-gamma">
        Go to the <a href="/" class="u-text-highlight  u-wf-500  u-bb-hover">homepage</a>
      </p>

    </div>

  </div>
</template>

<script setup lang="ts">
    import { computed, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { useAnnouncer } from '@vue-a11y/announcer';
    import { useHead } from '@unhead/vue';
    import SvgSprite from '@components/SvgSprite.vue';
    import { useRouterAccessibilityPattern } from '@composables/useRouterAccessibilityPattern';
    import { VueRouterMeta } from '@type/vue-router-meta.types';

    // Get route data
    const route = useRoute();

    // Get composables
    const { assertive } = useAnnouncer();
    const { useRouterFocus, useRouterTitle } = useRouterAccessibilityPattern();

    /**
     * Update the page title
     *
     * @returns Dynamic page title string
     */
     const metaTitle = computed(() => {
        return '404 Page Not Found – Tonight We Dance';
    });

    /**
     * Update the page description
     *
     * @returns Dynamic meta description string
     */
     const metaDescription = computed(() => {
        return 'We are sorry, the page you requested cannot be found. The URL may be misspelled or the page you’re looking for is no longer available.';
    });

    /**
     * Announce the route change to screen readers
     *
     * @returns Dynamic route announcement string
     */
     const routeAnnouncement = computed(() => {
        return '404 page not found has loaded';
    });

    // Set the page URL for og and canonical tag
    const pageURL = computed(() => {
        return window.location.href;
    });

    useHead({
        meta : [
            {
                name : 'description',
                content : metaDescription.value
            },
            {
                name : 'og:title',
                content : metaTitle.value,
                id : 'og-title'
            },
            {
                name : 'og:description',
                content : metaDescription.value,
                id : 'og-description'
            },
            {
                name : 'og:url',
                content : pageURL.value,
                id : 'og-url'
            }
        ],

        link : [{
            rel : 'canonical',
            href : pageURL.value
        }]
    });

    // Watch the Vue router object for changes
    watch(() => route, (to) => {

        // Get the Vue router meta data
        const toMeta: VueRouterMeta = to.meta as VueRouterMeta;

        // Update the page title on route change
        useRouterTitle(toMeta, metaTitle.value);

        // Set focus element on route change
        useRouterFocus();

        // Announce the route change for screen readers
        assertive(routeAnnouncement.value);
    }, { immediate : true });
</script>
